import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { notificacoesService, ctaDoEvento, rotuloDoEvento } from '@/services/notificacoesService'
import { notificacaoStore } from '@/store/notificacaoStore'
import { authStore } from '@/store/authStore'
import { ehConsultor } from '@/components/layout/menu'
import { NotificacaoDetalhe, NotificacaoResumo } from '@/types/notificacao'
import { formatDateTime } from '@/utils/formatters'
import { tokens } from '@/theme'

const TAMANHO_PAGINA = 20

/** Lida por ESTE usuário: `lida` responde para as de grupo; `status`/`lidaEm` para as pessoais. */
const estaLida = (n: NotificacaoResumo) => n.lida === true || n.status === 'LIDA' || !!n.lidaEm

type Filtro = 'naoLidas' | 'todas'

/**
 * Caixa de notificações do consultor (Onda 8 / A16, Figma "Notificações"). Fonte única:
 * `GET /v1/notificacoes` (o destinatário sai do token). Não lidas em destaque; abrir a
 * linha mostra o detalhe (`GET /{id}`) e marca como lida; o CTA vem do tipo de evento
 * (`CTA_POR_EVENTO`) — evento sem tela do consultor fica sem botão, não vai ao dashboard.
 *
 * `GET /v1/notificacoes` não filtra por status, então "Não lidas" é aplicado no cliente
 * sobre as páginas já carregadas. Por isso a contagem do chip vem de `/nao-lidas/count`
 * (a mesma do sino) e "Carregar anteriores" fica visível enquanto houver página seguinte,
 * inclusive quando a página atual não tem nenhuma não lida (R01).
 *
 * O back-office nunca chega aqui: `rotas/a14.tsx` despacha OPERADOR+ para a central.
 */
const NotificacoesPage: FC = () => {
  const consultor = ehConsultor(authStore((s) => s.usuario)?.permissoes)
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [itens, setItens] = useState<NotificacaoResumo[]>([])
  const [totalPaginas, setTotalPaginas] = useState(0)
  const [pagina, setPagina] = useState(0)
  const [carregando, setCarregando] = useState(true)
  const [carregandoMais, setCarregandoMais] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('naoLidas')
  /** Total de não lidas do backend (`/nao-lidas/count`); null = contagem indisponível. */
  const [totalNaoLidas, setTotalNaoLidas] = useState<number | null>(null)

  const [aberta, setAberta] = useState<NotificacaoResumo | null>(null)
  const [detalhe, setDetalhe] = useState<NotificacaoDetalhe | null>(null)
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)
  const [erroDetalhe, setErroDetalhe] = useState<string | null>(null)
  const [marcandoTodas, setMarcandoTodas] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    setErroStatus(null)
    try {
      const [page, count] = await Promise.all([
        notificacoesService.listar(0, TAMANHO_PAGINA),
        // A contagem falhando não derruba a caixa: o chip cai para o que está carregado.
        notificacoesService.contarNaoLidas().catch(() => null),
      ])
      setItens(page.content ?? [])
      setTotalPaginas(page.totalPages ?? 0)
      setPagina(0)
      setTotalNaoLidas(count)
      if (count !== null) notificacaoStore.getState().setNaoLidas(count)
    } catch (e) {
      const falha = e as { message?: string; status?: number } | null
      setErro(falha?.message ?? 'Falha ao carregar as notificações')
      setErroStatus(falha?.status ?? null)
      setItens([])
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const carregarMais = async () => {
    const proxima = pagina + 1
    setCarregandoMais(true)
    try {
      const page = await notificacoesService.listar(proxima, TAMANHO_PAGINA)
      setItens((atual) => [...atual, ...(page.content ?? [])])
      setTotalPaginas(page.totalPages ?? 0)
      setPagina(proxima)
    } catch {
      enqueueSnackbar('Não foi possível carregar mais notificações', { variant: 'error' })
    } finally {
      setCarregandoMais(false)
    }
  }

  const naoLidas = useMemo(() => itens.filter((n) => !estaLida(n)), [itens])
  const lista = filtro === 'naoLidas' ? naoLidas : itens
  /** Chip e "Marcar todas": contagem do backend quando existe, senão a das páginas carregadas. */
  const contagemNaoLidas = totalNaoLidas ?? naoLidas.length
  const temProximaPagina = pagina + 1 < totalPaginas

  /** Marca lida no backend e reflete na lista e no sino da topbar (sem esperar o polling). */
  const marcarLida = useCallback(async (n: NotificacaoResumo) => {
    if (estaLida(n)) return
    try {
      await notificacoesService.marcarLida(n.id)
      const agora = new Date().toISOString()
      setItens((atual) =>
        atual.map((x) => (x.id === n.id ? { ...x, lida: true, status: 'LIDA', lidaEm: agora } : x)),
      )
      setTotalNaoLidas((t) => (t === null ? t : Math.max(0, t - 1)))
      const store = notificacaoStore.getState()
      store.setNaoLidas(Math.max(0, store.naoLidas - 1))
    } catch {
      enqueueSnackbar('Não foi possível marcar como lida', { variant: 'error' })
    }
  }, [enqueueSnackbar])

  const marcarTodas = async () => {
    setMarcandoTodas(true)
    try {
      await notificacoesService.marcarTodasLidas()
      const agora = new Date().toISOString()
      setItens((atual) => atual.map((x) => ({ ...x, lida: true, status: 'LIDA', lidaEm: x.lidaEm ?? agora })))
      setTotalNaoLidas((t) => (t === null ? t : 0))
      notificacaoStore.getState().setNaoLidas(0)
      enqueueSnackbar('Todas as notificações foram marcadas como lidas', { variant: 'success' })
    } catch {
      enqueueSnackbar('Não foi possível marcar todas como lidas', { variant: 'error' })
    } finally {
      setMarcandoTodas(false)
    }
  }

  /** Abrir = detalhe + leitura. O detalhe falhando não impede a leitura nem o CTA. */
  const abrir = async (n: NotificacaoResumo) => {
    setAberta(n)
    setDetalhe(null)
    setErroDetalhe(null)
    setCarregandoDetalhe(true)
    void marcarLida(n)
    try {
      setDetalhe(await notificacoesService.detalhe(n.id))
    } catch {
      setErroDetalhe('Não foi possível carregar o conteúdo desta notificação.')
    } finally {
      setCarregandoDetalhe(false)
    }
  }

  const fechar = () => {
    setAberta(null)
    setDetalhe(null)
    setErroDetalhe(null)
  }

  const irParaCta = (n: NotificacaoResumo) => {
    const cta = ctaDoEvento(n.evento, consultor)
    if (!cta) return
    void marcarLida(n)
    navigate(cta.rota)
  }

  const colunas: ColunaTabelaAcao<NotificacaoResumo>[] = [
    {
      titulo: 'Notificação',
      render: (n) => {
        const lida = estaLida(n)
        return (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box
              aria-hidden
              sx={{
                mt: '7px',
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: lida ? 'transparent' : tokens.brandPrimary,
              }}
            />
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: lida ? 400 : 700, color: lida ? tokens.textSecondary : tokens.brandInk }}
              >
                {n.titulo}
              </Typography>
              {n.deGrupo && (
                <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                  Enviada ao seu papel
                </Typography>
              )}
            </Box>
          </Stack>
        )
      },
    },
    {
      titulo: 'Tipo',
      render: (n) => <Chip size="small" variant="outlined" label={rotuloDoEvento(n.evento)} />,
    },
    { titulo: 'Recebida em', render: (n) => formatDateTime(n.criadaEm) },
    {
      titulo: 'Status',
      render: (n) =>
        estaLida(n) ? (
          <Chip size="small" label="Lida" />
        ) : (
          <Chip size="small" color="primary" label="Não lida" />
        ),
    },
    {
      titulo: 'Ação sugerida',
      render: (n) => {
        const cta = ctaDoEvento(n.evento, consultor)
        return cta ? (
          <Button size="small" variant="text" onClick={() => irParaCta(n)} sx={{ whiteSpace: 'nowrap' }}>
            {cta.rotulo} →
          </Button>
        ) : (
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>—</Typography>
        )
      },
    },
  ]

  const ctaAberta = aberta ? ctaDoEvento(aberta.evento, consultor) : null

  return (
    <Box>
      <PageHeader
        title="Notificações"
        subtitle="Avisos sobre suas reservas, pendências, documentos e agenda"
        acao={
          <Button
            variant="outlined"
            onClick={() => void marcarTodas()}
            disabled={carregando || marcandoTodas || contagemNaoLidas === 0}
          >
            Marcar todas como lidas
          </Button>
        }
      />

      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filtro}
          onChange={(_, v: Filtro | null) => v && setFiltro(v)}
          aria-label="Filtro de notificações"
        >
          <ToggleButton value="naoLidas">Não lidas</ToggleButton>
          <ToggleButton value="todas">Todas</ToggleButton>
        </ToggleButtonGroup>
        {!carregando && !erro && (
          <Chip
            size="small"
            color={contagemNaoLidas > 0 ? 'primary' : 'default'}
            label={`${contagemNaoLidas} não lida(s)`}
          />
        )}
      </Stack>

      {carregando ? (
        <EstadoDeLista estado="carregando" mensagem="Carregando notificações…" />
      ) : erro ? (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          mensagem={erroStatus === 403 ? undefined : 'Não foi possível carregar as notificações'}
          detalhe={erroStatus === 403 ? undefined : erro}
          acao={erroStatus === 403 ? undefined : { label: 'Tentar novamente', onClick: () => void carregar() }}
        />
      ) : itens.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma notificação por aqui"
          detalhe="Os avisos sobre suas reservas, pendências e agenda aparecerão nesta caixa."
        />
      ) : (
        <>
          {lista.length === 0 ? (
            contagemNaoLidas === 0 ? (
              <EstadoDeLista
                estado="vazio"
                mensagem="Nenhuma notificação não lida"
                detalhe="Você está em dia. Veja as anteriores em “Todas”."
                acao={{ label: 'Ver todas', onClick: () => setFiltro('todas') }}
              />
            ) : (
              <EstadoDeLista
                estado="vazio"
                mensagem="Nenhuma não lida nas notificações já carregadas"
                detalhe={`O sino indica ${contagemNaoLidas} não lida(s) em notificações anteriores. Carregue as anteriores para vê-las.`}
                acao={{ label: 'Ver todas', onClick: () => setFiltro('todas') }}
              />
            )
          ) : (
            <TabelaAcao<NotificacaoResumo>
              aria-label="Notificações"
              colunas={colunas}
              linhas={lista}
              chaveDaLinha={(n) => n.id}
              onAbrir={(n) => void abrir(n)}
            />
          )}
          {temProximaPagina && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="text" onClick={() => void carregarMais()} disabled={carregandoMais}>
                {carregandoMais ? 'Carregando…' : 'Carregar anteriores'}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={!!aberta} onClose={fechar} maxWidth="sm" fullWidth aria-labelledby="notificacao-titulo">
        {aberta && (
          <>
            <DialogTitle id="notificacao-titulo" sx={{ fontWeight: 700 }}>
              {aberta.titulo}
            </DialogTitle>
            <DialogContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={rotuloDoEvento(aberta.evento)} />
                <Chip size="small" variant="outlined" label={`Recebida em ${formatDateTime(aberta.criadaEm)}`} />
                {aberta.canal && <Chip size="small" variant="outlined" label={aberta.canal} />}
              </Stack>
              {carregandoDetalhe ? (
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                  Carregando conteúdo…
                </Typography>
              ) : erroDetalhe ? (
                <Typography variant="body2" sx={{ color: tokens.statusDanger }} role="alert">
                  {erroDetalhe}
                </Typography>
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {detalhe?.conteudo || 'Esta notificação não tem conteúdo adicional.'}
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={fechar}>Fechar</Button>
              {ctaAberta && (
                <Button variant="contained" onClick={() => { fechar(); irParaCta(aberta) }}>
                  {ctaAberta.rotulo} →
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default NotificacoesPage
